import { Box, Card, Stack, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { ReportType } from "../../abstract/Report";
import HalfPieCard from "../../components/report/charts/HalfPieCard";
import { useApp } from "../../contexts/AppContext";
import { colors, fontSizes } from "../../theme";
import AnalysisTable, { Data as AnalysisTableData } from "./AnalysisTable";
import { GetCycleResponseItem } from "./types";

export const RateStatusCard: React.FC<{
  title: string;
  rate: number;
  caption: string;
}> = ({ title, rate, caption }) => {
  return (
    <Card sx={{ padding: 2, border: "1px solid #e0e0e0", borderRadius: 2, width: "100%" }}>
      <Stack gap={0} width={"100%"}>
        <Typography fontSize={fontSizes.lg} variant="medium">
          {title}
        </Typography>
        <Typography fontSize={fontSizes.lg} variant="medium">
          {rate}%
        </Typography>
        <Typography
          fontSize={fontSizes.sm}
          color={colors.slate[500]}
          variant="medium"
          gutterBottom
        >
          {caption}
        </Typography>
      </Stack>
    </Card>
  );
};

export const RateStatusCards: React.FC<{
  fertilizationRate: number;
  blastulationRate: number;
  maturityRate: number;
}> = ({ fertilizationRate, blastulationRate, maturityRate }) => {
  return (
    <Stack direction={"row"} gap={2}>
      <RateStatusCard
        title={"Maturity Rate"}
        rate={maturityRate || 0}
        caption={"of retrieved oocytes"}
      />
      <RateStatusCard
        title={"Fertilization Rate"}
        rate={fertilizationRate}
        caption={"of mature oocytes"}
      />
      <RateStatusCard
        title={"Blastulation Rate"}
        rate={blastulationRate}
        caption={"of fertilized oocytes"}
      />
    </Stack>
  );
};

export const AnalysisCards: React.FC<{
  aneuploidyRiskEstimate: number;
  liveBirthProbability: number;
}> = ({ aneuploidyRiskEstimate, liveBirthProbability }) => {
  const { translate } = useApp();
  if (aneuploidyRiskEstimate === null || liveBirthProbability === null) {
    return null;
  }

  return (
    <Stack gap={1} width={"100%"} id="analysis-cards">
      <Stack direction={"row"} gap={2}>
        <Box width={"100%"}>
          <HalfPieCard
            title={translate("embryo", "report.analysisCards.aneuploidy.title")}
            labels={[
              translate(
                "embryo",
                "report.analysisCards.aneuploidy.labels.rate"
              ),
              translate(
                "embryo",
                "report.analysisCards.aneuploidy.labels.normal"
              ),
            ]}
            rate={aneuploidyRiskEstimate}
          />
        </Box>
        <Box width={"100%"}>
          <HalfPieCard
            title={translate("embryo", "report.analysisCards.liveBirth.title")}
            labels={[
              translate(
                "embryo",
                "report.analysisCards.liveBirth.labels.liveBirth"
              ),
              translate(
                "embryo",
                "report.analysisCards.liveBirth.labels.noLiveBirth"
              ),
            ]}
            rate={liveBirthProbability}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export const OocyteAnalysis: React.FC<{
  type: ReportType;
  data: AnalysisTableData[];
}> = ({ data, type }) => {
  return <AnalysisTable type={type} data={data} />;
};

export const calculatedRatesAndProbabilitiesFn = (
  cycle: GetCycleResponseItem
) => {
  if (!cycle) {
    return {
      maturityRate: 0,
      fertilizationRate: 0,
      blastulationRate: 0,
      aneuploidyRiskEstimate: 0,
      liveBirthProbability: 0,
    };
  }

  const maturityRate =
    cycle.u_fertilized > 0
      ? (cycle.u_blastocyst / cycle.u_fertilized) * 100
      : 0;

  const fertilizationRate =
    cycle.images.reduce((sum, image) => sum + image.p_fertilization, 0) /
    cycle.images.length;

  const blastulationRate =
    cycle.images.reduce((sum, image) => sum + image.p_blastulation, 0) /
    cycle.images.length;

  return {
    maturityRate,
    fertilizationRate,
    blastulationRate,
    aneuploidyRiskEstimate: cycle.p_aneuploidy,
    liveBirthProbability: cycle.p_livebirth,
  };
};

const ReportInner: React.FC<{
  type: ReportType;
  cycle: GetCycleResponseItem;
}> = ({ type, cycle }) => {
  const [imageUrls, setImageUrls] = useState<{ [id: string]: string }>({});

  const calculatedRatesAndProbabilities = useMemo(() => {
    return calculatedRatesAndProbabilitiesFn(cycle);
  }, [cycle]);

  const cycleImages = useMemo<AnalysisTableData[]>(() => {
    if (!cycle) return [];

    return cycle.images.map((image, index) => ({
      image: imageUrls[image.id],
      index: index + 1,
      score: image.u_overall,
      fertilization: image.p_fertilization,
      blastulation: image.p_blastulation,
      invalid: image.invalid,
    }));
  }, [cycle, cycle?.images, imageUrls]);

  return (
    <Stack gap={2}>
      <AnalysisCards
        aneuploidyRiskEstimate={
          calculatedRatesAndProbabilities.aneuploidyRiskEstimate
        }
        liveBirthProbability={
          calculatedRatesAndProbabilities.liveBirthProbability
        }
      />
      <OocyteAnalysis type={type} data={cycleImages} />
    </Stack>
  );
};

export default ReportInner;
